import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  InputBase,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Business as BusinessIcon,
  SearchOutlined,
  MoreVert,
  EditOutlined,
  DeleteOutline,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import apiService from "../../service/Api";
import Pagination from "../../components/common/Pagination";
import { toast } from "react-toastify";

interface ICompany {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  industry?: string;
  logo?: string;
}
interface CompanyApiResponse {
  id: number;
  name: string;
  primary_email?: string | null;
  phone_number?: string | null;
  city?: string | null;
  country?: string | null;
  street_address?: string | null;
  company_type?: string | null;
  company_logo?: string | null;
}

const ListCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const primaryHoverColor = reduxTheme.primary_colors?.[600] || "#6b4e3d";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[100] || "#D6DDEB";
  const backgroundColor = reduxTheme.neutral_colors?.[900] || "#A9B2C3";

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    company: ICompany,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedCompany(company);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCompany(null);
  };

  const handleEditCompany = (companyId: number) => {
    navigate(`/companies/${companyId}/edit`);
    handleMenuClose();
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!companyId) return;

    // Optional: confirm deletion with the user
    const confirmed = window.confirm(
      "Are you sure you want to delete this company?",
    );
    if (!confirmed) return;

    try {
      await apiService.delete(`api/companies/company/${companyId}/`);
      // Refresh the list after deletion
      fetchCompanies();
    } catch (err) {
      console.error("Failed to delete company:", err);
      toast("Failed to delete company. Please try again.");
    } finally {
      handleMenuClose();
    }
  };

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number | undefined> = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
      };

      const res = await apiService.get<{
        count: number;
        results: CompanyApiResponse[];
      }>("api/companies/company/", params);

      const mappedCompanies: ICompany[] = res.data.results.map((company) => ({
        id: company.id,
        name: company.name,
        email: company.primary_email || undefined,
        phone: company.phone_number || undefined,
        address:
          company.city || company.country
            ? `${company.city || ""}, ${company.country || ""}`
                .trim()
                .replace(/^,|,$/g, "")
            : company.street_address || undefined,
        industry: company.company_type || undefined,
        logo: company.company_logo || undefined,
      }));

      setCompanies(mappedCompanies);
      setTotalItems(res.data.count);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to load companies.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const filteredCompanies = companies; // API already handles search

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Search & controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            pl: 1,
            flex: 1,
            maxWidth: 400,
          }}
        >
          <SearchOutlined sx={{ mr: 1, color: secondaryTextColor }} />
          <InputBase
            placeholder="Search companies..."
            sx={{ ml: 1, flex: 1, color: primaryTextColor }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
          <IconButton
            onClick={() => setViewMode("grid")}
            sx={{
              color: viewMode === "grid" ? primaryColor : secondaryTextColor,
            }}
          >
            <GridViewIcon />
          </IconButton>
          <IconButton
            onClick={() => setViewMode("list")}
            sx={{
              color: viewMode === "list" ? primaryColor : secondaryTextColor,
            }}
          >
            <ViewListIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/companies/add")}
            sx={{
              bgcolor: primaryColor,
              "&:hover": { bgcolor: primaryHoverColor },
              color: "#fff",
            }}
          >
            Add Company
          </Button>
        </Box>
      </Box>

      {/* Loading/Error */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
        >
          {error}
          <Button color="inherit" size="small" onClick={fetchCompanies}>
            Retry
          </Button>
        </Alert>
      )}

      {!isLoading && filteredCompanies.length === 0 && (
        <Typography sx={{ textAlign: "center", mt: 4 }}>
          No companies found.
        </Typography>
      )}

      {/* Grid view */}
      {viewMode === "grid" && filteredCompanies.length > 0 && (
        <>
          <Grid container spacing={2}>
            {filteredCompanies.map((company) => (
              <Grid size={{ xs: 12, md: 3 }} key={company.id}>
                <Card
                  sx={{
                    borderRadius: 3,
                    p: 2,
                    minHeight: 250,
                    boxShadow: 0,
                    position: "relative",
                    "&:hover .menu-button": {
                      opacity: 1,
                    },
                  }}
                >
                  <IconButton
                    className="menu-button"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      opacity: 0,
                      transition: "opacity 0.2s",
                      bgcolor: "rgba(255,255,255,0.8)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                    }}
                    onClick={(e) => handleMenuClick(e, company)}
                  >
                    <MoreVert />
                  </IconButton>

                  {/* Company avatar/content */}
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Avatar
                      src={company.logo}
                      sx={{ width: 80, height: 80, mb: 2 }}
                    >
                      {!company.logo && <BusinessIcon fontSize="large" />}
                    </Avatar>
                    <Typography variant="h6" align="center">
                      {company.name}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {company.email || "-"}
                    </Typography>
                    <Typography variant="body2" align="center">
                      {company.industry || "Company"}
                    </Typography>
                  </CardContent>

                  <CardActions sx={{ justifyContent: "center" }}>
                    <Button
                      onClick={() => navigate(`/companies/${company.id}`)}
                      sx={{ bgcolor: "#724E41", color: "#fff" }}
                    >
                      View Company
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() =>
                selectedCompany && handleEditCompany(selectedCompany.id)
              }
            >
              <EditOutlined sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedCompany && handleDeleteCompany(selectedCompany.id)
              }
            >
              <DeleteOutline sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>

          {!isLoading && (
            <Box sx={{ mt: 4 }}>
              <Pagination
                currentPage={currentPage - 1} // convert to 0-based for UI
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={(page) => setCurrentPage(page + 1)} // convert back to 1-based
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* List view */}
      {viewMode === "list" && filteredCompanies.length > 0 && (
        <Box>
          {filteredCompanies.map((company) => (
            <Card
              key={company.id}
              sx={{ mb: 2, p: 2, display: "flex", alignItems: "center" }}
            >
              <Avatar src={company.logo} sx={{ width: 50, height: 50, mr: 2 }}>
                {!company.logo && <BusinessIcon />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{company.name}</Typography>
                <Typography variant="body2">{company.email}</Typography>
                <Typography variant="body2">{company.industry}</Typography>
              </Box>
              <Button
                onClick={() => navigate(`/companies/${company.id}`)}
                sx={{ bgcolor: "#724E41", color: "#fff" }}
              >
                View Company
              </Button>
            </Card>
          ))}
        </Box>
      )}
    </motion.div>
  );
};

export default ListCompanies;
